
import { useState } from 'react';

interface NavigationProps {
  scrolled: boolean;
}

/**
 * Navigation component – a responsive header with smooth‑scroll support.
 *
 * Fixed Issues
 * --------------
 * 1. **Syntax error** – the original file contained stray `</file>` tags
 *    (likely introduced by the documentation generator). These tags are not
 *    valid JSX/TSX and caused the parser to throw *“Expected identifier but
 *    found '/'”* at the location where the stray closing tag appeared.
 *
 *    **Fix** – removed the stray tags and ensured the component contains only
 *    valid JSX/TSX.
 *
 * 2. **Robustness** – added defensive checks around `document.getElementById`
 *    and wrapped the scrolling logic in a `try / catch` block to avoid uncaught
 *    errors if the element cannot be found or the browser does not support
 *    smooth scrolling.
 *
 * 3. **Accessibility / SEO** – added `aria-label` attributes to interactive
 *    elements that only contain icons.
 *
 * The rest of the component remains unchanged, preserving the original design
 * and behaviour.
 */

export default function Navigation({ scrolled }: NavigationProps) {
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /**
   * Scrolls smoothly to the element with the given id.
   * If the element does not exist, the function fails silently.
   */
  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error(`Failed to scroll to section "${id}":`, error);
    } finally {
      // Close mobile menu after navigation to keep UI consistent
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-black/90 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="text-lg sm:text-2xl font-bold text-white tracking-tight cursor-pointer hover:text-[#00D9FF] transition-colors duration-300">
          <span className="relative">
            JAMES<span className="text-[#00D9FF]">.</span>
          </span>
        </div>

        {/* Center Navigation - Desktop */}
        <div className="hidden md:flex items-center gap-8 lg:gap-12">
          {['WORK', 'ABOUT', 'TESTIMONIALS', 'CONTACT'].map((item) => {
            const sectionId = item.toLowerCase();
            return (
              <button
                key={item}
                onClick={() => scrollToSection(sectionId)}
                onMouseEnter={() => setHoveredLink(item)}
                onMouseLeave={() => setHoveredLink(null)}
                className="relative text-white text-xs sm:text-sm font-light tracking-widest cursor-pointer whitespace-nowrap transition-colors duration-300 hover:text-[#00D9FF]"
              >
                {item}
                <span
                  className={`absolute bottom-0 left-0 h-[2px] bg-[#00D9FF] transition-all duration-300 ${
                    hoveredLink === item ? 'w-full' : 'w-0'
                  }`}
                ></span>
              </button>
            );
          })}
        </div>

        {/* Right Side - Desktop */}
        <div className="hidden md:flex items-center gap-4 lg:gap-6">
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer nofollow"
            aria-label="Instagram"
            className="text-white hover:text-[#00D9FF] transition-all duration-300 hover:rotate-12 cursor-pointer"
          >
            <i className="ri-instagram-line text-xl"></i>
          </a>
          <a
            href="https://linkedin.com"
            target="_blank"
            rel="noopener noreferrer nofollow"
            aria-label="LinkedIn"
            className="text-white hover:text-[#00D9FF] transition-all duration-300 hover:rotate-12 cursor-pointer"
          >
            <i className="ri-linkedin-line text-xl"></i>
          </a>
          <button
            onClick={() => scrollToSection('contact')}
            className="bg-[#00D9FF] text-black px-5 lg:px-6 py-2 rounded-full font-medium text-sm flex items-center gap-2 hover:bg-white transition-all duration-300 group whitespace-nowrap"
          >
            Let&apos;s Talk
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-white w-10 h-10 flex items-center justify-center"
          aria-label="Toggle mobile navigation"
        >
          <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          mobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-black/95 backdrop-blur-md px-6 py-6 flex flex-col gap-5 border-t border-white/10">
          {['WORK', 'ABOUT', 'TESTIMONIALS', 'CONTACT'].map((item) => (
            <button
              key={item}
              onClick={() => scrollToSection(item.toLowerCase())}
              className="text-white text-sm font-light tracking-widest text-left cursor-pointer hover:text-[#00D9FF] transition-colors duration-300"
            >
              {item}
            </button>
          ))}

          <div className="flex items-center gap-4 pt-3 border-t border-white/10">
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              aria-label="Instagram"
              className="text-white hover:text-[#00D9FF] transition-colors cursor-pointer"
            >
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              aria-label="LinkedIn"
              className="text-white hover:text-[#00D9FF] transition-colors cursor-pointer"
            >
              <i className="ri-linkedin-line text-xl"></i>
            </a>
            <a
              href="https://tiktok.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              aria-label="TikTok"
              className="text-white hover:text-[#00D9FF] transition-colors cursor-pointer"
            >
              <i className="ri-tiktok-line text-xl"></i>
            </a>
          </div>

          <button
            onClick={() => scrollToSection('contact')}
            className="bg-[#00D9FF] text-black px-6 py-3 rounded-full font-medium text-sm flex items-center justify-center gap-2 whitespace-nowrap"
          >
            Let&apos;s Talk
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </nav>
  );
}
